/**
 * Context provider that loads and applies tenant i18n overrides.
 * Must be placed inside AuthProvider (needs tenant context)
 * and should wrap the main app content.
 */
import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth';
import { i18nOverridesApi } from '../../modules/admin/services/i18nOverridesApi';
import { applyTenantI18nOverrides } from '../../i18n/config';

const I18N_LOADER_QUERY_KEY = 'i18n-loader';

export function I18nOverridesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';

  const { data } = useQuery({
    queryKey: [I18N_LOADER_QUERY_KEY, locale],
    queryFn: () => i18nOverridesApi.getLoaderData(locale),
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (data) {
      applyTenantI18nOverrides(locale, data.terms, data.overrides);
    }
  }, [data, locale]);

  return <>{children}</>;
}
